import type { NuggetSeverity } from "@/constants";
import { normalizeNuggetSeverity } from "@/features/nuggets/model/nuggetSeverity";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Reads back what the server actually stored, so the caller reconciles rather than assumes. */
function toStoredSeverity(data: unknown): NuggetSeverity | null {
  if (!isRecord(data) || !isRecord(data.details)) {
    throw new Error("Unexpected response shape.");
  }
  return normalizeNuggetSeverity(data.details.severity);
}

/**
 * Updates one nugget's severity via the generic nugget PATCH.
 *
 * `PATCH /v1/nuggets/{id}` is a partial update — fields absent from the body are left alone — and it
 * responds with the whole nugget. Only `details.severity` is read back here.
 *
 * Deliberately does not import a runtime value from `nuggetsSlice`: the slice imports this module,
 * so a runtime import back would close a cycle (`nuggetFeedbackApi` avoids it the same way, by
 * importing types only).
 */
export async function setNuggetSeverity(
  nuggetId: number,
  severity: NuggetSeverity,
): Promise<NuggetSeverity | null> {
  const res = await apiFetch(`/v1/nuggets/${nuggetId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ severity }),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return toStoredSeverity(await res.json());
}
